import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Check, Mail, Phone, MapPin } from "lucide-react";
import { Rise } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = ["Book a demo", "Licensing & pricing", "Onboarding & training", "Managed QA"];

export const LeadForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    interest: "Book a demo",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("Thanks — we'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", phone: "", interest: "Book a demo", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-border py-3 text-foreground placeholder:text-muted-foreground/60 outline-none transition-colors duration-300 focus:border-foreground";

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-32">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid gap-14 lg:grid-cols-2">
          <Rise>
            <span className="font-mono-ui text-[11px] font-bold uppercase tracking-[0.2em] text-primary">
              Get started
            </span>
            <h2 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter leading-[0.95]">
              Let's automate <br />
              your QA<span className="text-primary">.</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground">
              Tell us about your stack and goals. We'll set up a tailored FireFlink demo and a
              clear path to rollout — no obligation.
            </p>

            <div className="mt-10 space-y-5 border-t border-border pt-8">
              <a
                href="mailto:info@interlockedsolutions.co.uk"
                data-testid="contact-email-link"
                className="group flex items-center gap-3.5 text-foreground transition-colors hover:text-primary"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary transition-colors duration-300 group-hover:bg-primary/20">
                  <Mail size={16} strokeWidth={1.75} />
                </span>
                <span className="font-heading text-lg tracking-tight">info@interlockedsolutions.co.uk</span>
              </a>
              <a
                href="tel:+447404784910"
                data-testid="contact-phone-link"
                className="group flex items-center gap-3.5 text-foreground transition-colors hover:text-primary"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary transition-colors duration-300 group-hover:bg-primary/20">
                  <Phone size={16} strokeWidth={1.75} />
                </span>
                <span className="font-heading text-lg tracking-tight">+44 7404 784910</span>
              </a>
              <div className="flex items-center gap-3.5 text-muted-foreground">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary/10 text-secondary">
                  <MapPin size={16} strokeWidth={1.75} />
                </span>
                <span className="text-sm">United Kingdom · Mon–Fri, 9am–6pm GMT</span>
              </div>
            </div>
          </Rise>

          <Rise delay={0.1}>
            {done ? (
              <div
                data-testid="lead-success"
                className="flex h-full min-h-[420px] flex-col items-center justify-center rounded-2xl border border-border bg-card p-10 text-center"
              >
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-primary/15 text-primary">
                  <Check size={26} strokeWidth={2} />
                </div>
                <h3 className="font-heading text-2xl font-medium tracking-tight">Request received</h3>
                <p className="mt-3 max-w-xs text-sm text-muted-foreground">
                  A member of the Interlocked Solutions team will reach out within one business day.
                </p>
                <button
                  data-testid="lead-reset-button"
                  onClick={() => setDone(false)}
                  className="mt-8 text-sm font-bold text-primary underline underline-offset-4"
                >
                  Submit another request
                </button>
              </div>
            ) : (
              <form
                data-testid="lead-form"
                onSubmit={submit}
                className="rounded-2xl border border-border bg-card p-8 lg:p-10"
              >
                <div className="grid gap-6 sm:grid-cols-2">
                  <input
                    data-testid="lead-input-name"
                    className={inputCls}
                    placeholder="Full name *"
                    value={form.name}
                    onChange={update("name")}
                  />
                  <input
                    data-testid="lead-input-email"
                    type="email"
                    className={inputCls}
                    placeholder="Work email *"
                    value={form.email}
                    onChange={update("email")}
                  />
                  <input
                    data-testid="lead-input-company"
                    className={inputCls}
                    placeholder="Company"
                    value={form.company}
                    onChange={update("company")}
                  />
                  <input
                    data-testid="lead-input-phone"
                    className={inputCls}
                    placeholder="Phone"
                    value={form.phone}
                    onChange={update("phone")}
                  />
                </div>

                <div className="mt-8">
                  <label className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    I'm interested in
                  </label>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {INTERESTS.map((it) => (
                      <button
                        key={it}
                        type="button"
                        data-testid={`lead-interest-${it.split(" ")[0].toLowerCase()}`}
                        onClick={() => setForm((f) => ({ ...f, interest: it }))}
                        className={`rounded-full border px-4 py-2 text-xs transition-colors duration-300 ${
                          form.interest === it
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
                        }`}
                      >
                        {it}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-8">
                  <textarea
                    data-testid="lead-input-message"
                    rows={3}
                    className={`${inputCls} resize-none`}
                    placeholder="Tell us about your project (optional)"
                    value={form.message}
                    onChange={update("message")}
                  />
                </div>

                <button
                  data-testid="lead-submit-button"
                  type="submit"
                  disabled={loading}
                  className="group mt-10 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-7 py-4 text-sm font-medium text-primary-foreground transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 size={17} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Request your demo
                      <ArrowRight
                        size={17}
                        className="transition-transform duration-300 group-hover:translate-x-1"
                      />
                    </>
                  )}
                </button>
              </form>
            )}
          </Rise>
        </div>
      </div>
    </section>
  );
};
